/**
 * BMKO POS Offline Storage & Synchronization Engine
 *
 * Provides high-speed IndexedDB caching for product catalogs,
 * zero-latency offline barcode/text search, local offline order creation,
 * thermal invoice preparation, and background sync queue replay.
 */

const DB_NAME = "bmko_offline_pos_db";
const DB_VERSION = 1;

/**
 * Open or upgrade the client IndexedDB
 * @returns {Promise<IDBDatabase>}
 */
export function openOfflineDB() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      return reject(new Error("IndexedDB is not supported in this environment"));
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // 1. Products catalog store
      if (!db.objectStoreNames.contains("products")) {
        const productStore = db.createObjectStore("products", { keyPath: "id" });
        productStore.createIndex("barcode", "barcode", { unique: false });
        productStore.createIndex("sku", "sku", { unique: false });
        productStore.createIndex("name_lower", "name_lower", { unique: false });
      }

      // 2. Sync events queue store
      if (!db.objectStoreNames.contains("sync_queue")) {
        const queueStore = db.createObjectStore("sync_queue", { keyPath: "client_event_id" });
        queueStore.createIndex("status", "status", { unique: false });
        queueStore.createIndex("created_at", "created_at", { unique: false });
        queueStore.createIndex("client_seq", "client_seq", { unique: false });
      }

      // 3. Offline orders snapshots
      if (!db.objectStoreNames.contains("offline_orders")) {
        const orderStore = db.createObjectStore("offline_orders", { keyPath: "offline_order_number" });
        orderStore.createIndex("created_at", "created_at", { unique: false });
        orderStore.createIndex("synced", "synced", { unique: false });
      }

      // 4. Metadata key-value store
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get or create unique persistent device terminal ID
 */
export function getDeviceId() {
  if (typeof window === "undefined") return "server-terminal-001";
  let devId = localStorage.getItem("bmko_pos_device_id");
  if (!devId) {
    devId = "POS-DEV-" + Math.random().toString(36).substring(2, 9).toUpperCase();
    localStorage.setItem("bmko_pos_device_id", devId);
  }
  return devId;
}

/**
 * Get monotonic client sequence number
 */
export function getNextSequence() {
  if (typeof window === "undefined") return 1;
  const current = parseInt(localStorage.getItem("bmko_pos_seq") || "0", 10) + 1;
  localStorage.setItem("bmko_pos_seq", current.toString());
  return current;
}

/**
 * Bulk cache products into IndexedDB for zero-latency offline scanning
 * @param {Array} productsList
 * @returns {Promise<number>} count of cached items
 */
export async function cacheProductsCatalog(productsList = []) {
  if (!Array.isArray(productsList) || productsList.length === 0) return 0;
  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(["products", "meta"], "readwrite");
    const productStore = tx.objectStore("products");
    const metaStore = tx.objectStore("meta");

    let count = 0;
    for (const item of productsList) {
      const id = item.id || item._id;
      if (!id) continue;

      const record = {
        ...item,
        id: String(id),
        name_lower: (item.name || "").toLowerCase().trim(),
        barcode: item.barcode ? String(item.barcode).trim() : "",
        sku: item.sku ? String(item.sku).trim() : "",
      };

      productStore.put(record);
      count++;
    }

    metaStore.put({
      key: "last_catalog_sync",
      value: new Date().toISOString(),
      count,
    });

    tx.oncomplete = () => resolve(count);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Search local IndexedDB catalog by barcode, SKU, or name
 * @param {string} query
 * @param {number} limit
 * @returns {Promise<Array>} matching products
 */
export async function searchOfflineCatalog(query = "", limit = 25) {
  const cleanQ = (query || "").trim().toLowerCase();
  if (!cleanQ) return [];

  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction("products", "readonly");
    const store = tx.objectStore("products");
    const results = [];
    const exactMatches = [];

    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const prod = cursor.value;
        const b = (prod.barcode || "").toLowerCase();
        const s = (prod.sku || "").toLowerCase();
        const n = prod.name_lower || "";

        // Check variants if present
        let variantMatch = false;
        if (Array.isArray(prod.variants)) {
          for (const v of prod.variants) {
            const vb = (v.barcode || "").toLowerCase();
            const vs = (v.sku || "").toLowerCase();
            const vn = (v.name || "").toLowerCase();
            if (vb === cleanQ || vs === cleanQ) {
              exactMatches.unshift(prod);
              variantMatch = true;
              break;
            } else if (vb.includes(cleanQ) || vs.includes(cleanQ) || vn.includes(cleanQ)) {
              results.push(prod);
              variantMatch = true;
              break;
            }
          }
        }

        if (!variantMatch) {
          if (b === cleanQ || s === cleanQ) {
            exactMatches.unshift(prod);
          } else if (b.includes(cleanQ) || s.includes(cleanQ) || n.includes(cleanQ)) {
            results.push(prod);
          }
        }

        // Limit results check
        if (exactMatches.length + results.length >= limit * 2) {
          const combined = [...exactMatches, ...results].slice(0, limit);
          resolve(combined);
          return;
        }

        cursor.continue();
      } else {
        const combined = [...exactMatches, ...results].slice(0, limit);
        resolve(combined);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Get count and sync time of cached offline catalog
 */
export async function getCachedCatalogMeta() {
  const db = await openOfflineDB();
  return new Promise((resolve) => {
    const tx = db.transaction(["products", "meta"], "readonly");
    const productStore = tx.objectStore("products");
    const metaStore = tx.objectStore("meta");

    let count = 0;
    let lastSync = null;

    const countReq = productStore.count();
    countReq.onsuccess = () => {
      count = countReq.result;
    };

    const metaReq = metaStore.get("last_catalog_sync");
    metaReq.onsuccess = () => {
      if (metaReq.result) {
        lastSync = metaReq.result.value;
      }
    };

    tx.oncomplete = () => resolve({ count, lastSync });
    tx.onerror = () => resolve({ count: 0, lastSync: null });
  });
}

/**
 * Generate unique offline invoice number
 */
export function generateOfflineOrderNumber(storeCode = "MSB") {
  const ts = Math.floor(Date.now() / 1000).toString(36).toUpperCase();
  const seq = String(getNextSequence()).padStart(4, "0");
  return `OFF-${storeCode}-${ts}-${seq}`;
}

/**
 * Create and enqueue an offline POS order
 * @param {object} param0
 * @returns {Promise<object>} { offlineOrder, clientEventId }
 */
export async function queueOfflineOrder({
  checkoutPayload,
  items = [],
  totals = {},
  selectedCustomer = null,
  store = null,
  salesman = "CASHIER",
}) {
  const db = await openOfflineDB();
  const storeCode = (store?.code || store?.name?.substring(0, 3) || "MSB").toUpperCase();
  const offlineOrderNumber = generateOfflineOrderNumber(storeCode);
  const clientEventId = typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `evt-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  const deviceId = getDeviceId();
  const clientSeq = getNextSequence();
  const nowIso = new Date().toISOString();

  // Create local order structure matching standard backend order response for thermal PDF
  const offlineOrder = {
    id: offlineOrderNumber,
    _id: offlineOrderNumber,
    order_number: offlineOrderNumber,
    offline_order_number: offlineOrderNumber,
    is_offline: true,
    status: "confirmed",
    channel: "pos",
    created_at: nowIso,
    customer: selectedCustomer
      ? {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          phone: selectedCustomer.phone || "",
          email: selectedCustomer.email || "",
        }
      : { name: "Walk-in Customer", phone: "" },
    items: items.map((it) => ({
      product_id: it.productId,
      variant_id: it.variantId || null,
      name: it.productName,
      sku: it.itemcode,
      barcode: it.itemcode,
      quantity: it.qty,
      unit_price: it.unitCost,
      mrp: it.mrp,
      line_total: it.netAmount,
      tax_rate: it.taxRate || 0,
    })),
    pricing: {
      subtotal: totals.subtotal || totals.grandTotal,
      discount: totals.totalDiscount || 0,
      tax: totals.tax || 0,
      total: totals.grandTotal || 0,
      packing_charge: Number(checkoutPayload.packing_charge) || 0,
      delivery_charge: Number(checkoutPayload.delivery_charge) || 0,
      service_charge: Number(checkoutPayload.service_charge) || 0,
    },
    payment: {
      method: checkoutPayload.payment_method || "cash",
      status: "completed",
      amount: totals.grandTotal || 0,
      split_breakdown: checkoutPayload.split_breakdown || null,
      amount_due: checkoutPayload.payment_method === "credit" ? totals.grandTotal : 0,
    },
    metadata: {
      device_id: deviceId,
      salesman: salesman,
      offline: true,
      pending_sync: true,
    },
    note: checkoutPayload.notes || "",
  };

  // Prepare sync queue event
  const syncEvent = {
    client_event_id: clientEventId,
    device_id: deviceId,
    client_seq: clientSeq,
    entity_type: "order",
    action: "create",
    payload: {
      ...checkoutPayload,
      offline_order_number: offlineOrderNumber,
      client_timestamp: nowIso,
    },
    status: "queued", // 'queued' | 'in_flight' | 'applied' | 'failed'
    offline_order_number: offlineOrderNumber,
    order_total: totals.grandTotal || 0,
    created_at: nowIso,
    retries: 0,
    error: null,
  };

  return new Promise((resolve, reject) => {
    const tx = db.transaction(["sync_queue", "offline_orders"], "readwrite");
    const queueStore = tx.objectStore("sync_queue");
    const orderStore = tx.objectStore("offline_orders");

    queueStore.put(syncEvent);
    orderStore.put({
      offline_order_number: offlineOrderNumber,
      client_event_id: clientEventId,
      synced: false,
      created_at: nowIso,
      order: offlineOrder,
    });

    tx.oncomplete = () => resolve({ offlineOrder, clientEventId });
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Query statistics of offline queue
 * @returns {Promise<object>}
 */
export async function getOfflineQueueStats() {
  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction("sync_queue", "readonly");
    const store = tx.objectStore("sync_queue");
    const stats = {
      queued: 0,
      in_flight: 0,
      applied: 0,
      failed: 0,
      pendingRevenue: 0,
    };

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const item = cursor.value;
        if (item.status === "queued") {
          stats.queued++;
          stats.pendingRevenue += Number(item.order_total || 0);
        } else if (item.status === "in_flight") {
          stats.in_flight++;
          stats.pendingRevenue += Number(item.order_total || 0);
        } else if (item.status === "applied") {
          stats.applied++;
        } else if (item.status === "failed") {
          stats.failed++;
          stats.pendingRevenue += Number(item.order_total || 0);
        }
        cursor.continue();
      } else {
        stats.pendingRevenue = Number(stats.pendingRevenue.toFixed(2));
        resolve(stats);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Fetch all local offline orders for review/reprint
 * @param {number} limit
 * @returns {Promise<Array>}
 */
export async function getOfflineOrders(limit = 50) {
  const db = await openOfflineDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction("offline_orders", "readonly");
    const store = tx.objectStore("offline_orders");
    const orders = [];

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        orders.unshift(cursor.value);
        if (orders.length >= limit) {
          resolve(orders);
          return;
        }
        cursor.continue();
      } else {
        resolve(orders);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Push pending offline queue events to server sync route
 * @param {object} apiInstance - Axios or custom API wrapper
 * @returns {Promise<object>} { syncedCount, failedCount, results }
 */
export async function syncPendingQueue(apiInstance) {
  if (!navigator.onLine) {
    return { syncedCount: 0, failedCount: 0, error: "Network offline" };
  }

  const db = await openOfflineDB();

  // 1. Fetch queued or retryable failed events
  const pendingEvents = await new Promise((resolve, reject) => {
    const tx = db.transaction("sync_queue", "readonly");
    const store = tx.objectStore("sync_queue");
    const items = [];

    const request = store.openCursor();
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const row = cursor.value;
        if (row.status === "queued" || (row.status === "failed" && row.retries < 5)) {
          items.push(row);
        }
        cursor.continue();
      } else {
        resolve(items);
      }
    };
    request.onerror = () => reject(request.error);
  });

  if (pendingEvents.length === 0) {
    return { syncedCount: 0, failedCount: 0, message: "Queue is empty" };
  }

  // 2. Mark events as in_flight
  await new Promise((resolve, reject) => {
    const tx = db.transaction("sync_queue", "readwrite");
    const store = tx.objectStore("sync_queue");
    for (const evt of pendingEvents) {
      store.put({ ...evt, status: "in_flight" });
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  // 3. Assemble push batch envelope
  const deviceId = getDeviceId();
  const batchId = `BATCH-${Date.now()}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;
  const batchPayload = {
    batch_id: batchId,
    device_id: deviceId,
    events: pendingEvents.map((e) => ({
      client_event_id: e.client_event_id,
      entity_type: e.entity_type,
      action: e.action,
      payload: e.payload,
      created_at: e.created_at,
    })),
  };

  try {
    const res = await apiInstance.post("/sync/push", batchPayload);
    const syncResult = res.data; // { batch_id, accepted, failed, results }
    const resultLookup = {};
    if (Array.isArray(syncResult?.results)) {
      for (const r of syncResult.results) {
        resultLookup[r.client_event_id] = r;
      }
    }

    let syncedCount = 0;
    let failedCount = 0;

    // 4. Update sync_queue and offline_orders in IndexedDB
    await new Promise((resolve, reject) => {
      const tx = db.transaction(["sync_queue", "offline_orders"], "readwrite");
      const queueStore = tx.objectStore("sync_queue");
      const orderStore = tx.objectStore("offline_orders");

      for (const evt of pendingEvents) {
        const ack = resultLookup[evt.client_event_id];
        if (ack && (ack.status === "applied" || ack.status === "idempotent")) {
          syncedCount++;
          queueStore.put({
            ...evt,
            status: "applied",
            server_id: ack.server_id || null,
            server_order_number: ack.order_number || null,
            synced_at: new Date().toISOString(),
          });

          // Update local order snapshot
          if (evt.offline_order_number) {
            const getOrderReq = orderStore.get(evt.offline_order_number);
            getOrderReq.onsuccess = () => {
              if (getOrderReq.result) {
                const rec = getOrderReq.result;
                rec.synced = true;
                rec.server_order_number = ack.order_number;
                if (rec.order) {
                  rec.order.server_order_number = ack.order_number;
                  rec.order.is_synced = true;
                }
                orderStore.put(rec);
              }
            };
          }
        } else {
          failedCount++;
          queueStore.put({
            ...evt,
            status: "failed",
            error: ack?.error || "Rejection during sync push",
            retries: (evt.retries || 0) + 1,
          });
        }
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    return {
      syncedCount,
      failedCount,
      accepted: syncResult?.accepted || syncedCount,
      results: syncResult?.results || [],
    };
  } catch (err) {
    // Revert in_flight items back to queued on network or unexpected error
    await new Promise((resolve) => {
      const tx = db.transaction("sync_queue", "readwrite");
      const store = tx.objectStore("sync_queue");
      for (const evt of pendingEvents) {
        store.put({
          ...evt,
          status: "queued",
          error: err.message || "Network error during sync",
        });
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });

    throw err;
  }
}
